import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSubscription } from '@/contexts/SubscriptionContext';
import {
  Lock,
  Zap,
  Bot,
  Volume2,
  Radio,
  Shield,
  Bell,
  MapPin,
  Crown,
  Loader2
} from 'lucide-react';

// Feature descriptions for upgrade prompts
const FEATURE_INFO = {
  ai_chat: {
    icon: Bot,
    title: 'Tank & Daisy AI Assistants',
    description: 'Get 24/7 access to your personal intelligence team. Tank provides tactical briefings while Daisy guides you through emergencies.',
    benefit: 'Like having a former intel analyst on call'
  },
  voice_alerts: {
    icon: Volume2,
    title: 'Voice Alerts & Briefings',
    description: 'Hear critical alerts read aloud by Tank or Daisy. Perfect for hands-free awareness while driving or working.',
    benefit: 'Never miss an alert again'
  },
  frequencies: {
    icon: Radio,
    title: 'All 50 States Frequencies',
    description: 'Access the complete database of police, fire, and EMS frequencies for every state. Travel prepared.',
    benefit: 'Full situational awareness anywhere'
  },
  real_time_alerts: {
    icon: Bell,
    title: 'Real-Time Push Alerts',
    description: 'Get instant notifications when incidents occur near your saved locations. Know before your neighbors.',
    benefit: 'Early warning saves lives'
  },
  threat_analysis: {
    icon: Shield,
    title: 'Tank\'s OverWatch - Visual Intelligence',
    description: 'Access live traffic cameras correlated with scanner activity. Tank sees what\'s happening, not just what\'s being reported.',
    benefit: 'See and hear - full situational awareness'
  },
  saved_locations: {
    icon: MapPin,
    title: 'Unlimited Saved Locations',
    description: 'Monitor your home, work, family homes, and bug-out routes. Keep everyone in your circle informed.',
    benefit: 'Protect everywhere that matters'
  },
  export_frequencies: {
    icon: Radio,
    title: 'Export to Scanner',
    description: 'Download frequencies in formats compatible with your scanner or SDR software.',
    benefit: 'Program your gear in seconds'
  }
};

// Inline upgrade prompt (for use within pages) - Now with trial button
export const UpgradePrompt = ({ 
  feature, 
  compact = false,
  className = '' 
}) => {
  const { tier, canStartTrial, startTrial } = useSubscription();
  const [loading, setLoading] = React.useState(false);
  
  const info = FEATURE_INFO[feature] || {
    icon: Lock,
    title: 'Pro Feature',
    description: 'This feature is available with VeriWatch Pro.',
    benefit: 'Upgrade to unlock'
  };
  const Icon = info.icon;
  
  const handleStartTrial = async () => {
    setLoading(true);
    await startTrial();
    setLoading(false);
  };
  
  if (compact) {
    return (
      <div className={`bg-slate-800/50 border border-slate-700 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-500/20 rounded-lg">
            <Lock className="w-5 h-5 text-teal-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-white">{info.title}</p>
            <p className="text-xs text-slate-400">{info.benefit}</p>
          </div>
          {canStartTrial() ? (
            <Button 
              size="sm" 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleStartTrial}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
              Try Free
            </Button>
          ) : (
            <Link to="/pricing">
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700">
                <Zap className="w-3 h-3 mr-1" />
                Upgrade
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className={`bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700 rounded-xl p-6 text-center ${className}`}>
      <div className="inline-flex p-3 bg-teal-500/20 rounded-full mb-4">
        <Icon className="w-8 h-8 text-teal-400" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2">{info.title}</h3>
      <p className="text-slate-400 mb-4 max-w-md mx-auto">{info.description}</p>
      <p className="text-teal-400 text-sm mb-6">{info.benefit}</p>
      
      {/* Trial Button - Primary CTA */}
      {canStartTrial() && (
        <Button 
          onClick={handleStartTrial}
          disabled={loading}
          className="w-full max-w-xs bg-orange-600 hover:bg-orange-700 mb-3 font-bold"
          data-testid="upgrade-prompt-start-trial"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Activating...
            </span>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              24-Hour Tactical Preview - GO!
            </>
          )}
        </Button>
      )}
      
      <Link to="/pricing">
        <Button className={`${canStartTrial() ? 'bg-slate-700 hover:bg-slate-600' : 'bg-teal-600 hover:bg-teal-700'}`}>
          <Zap className="w-4 h-4 mr-2" />
          {canStartTrial() ? 'Or Upgrade to Pro - $10/month' : 'Upgrade to Pro - $10/month'}
        </Button>
      </Link>
      <p className="text-xs text-slate-500 mt-3">
        {canStartTrial() ? 'No credit card required for preview' : '$80/year (save $40) • $199 lifetime'}
      </p>
    </div>
  );
};

// Modal upgrade prompt
export const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  feature,
  title,
  description 
}) => {
  const info = FEATURE_INFO[feature] || {
    icon: Lock,
    title: title || 'Pro Feature',
    description: description || 'This feature is available with VeriWatch Pro.'
  };
  const Icon = info.icon;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-teal-500/20 rounded-full">
              <Icon className="w-10 h-10 text-teal-400" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            {info.title}
          </DialogTitle>
          <DialogDescription className="text-center text-slate-400">
            {info.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div className="bg-slate-800/50 rounded-lg p-4">
            <p className="text-sm text-slate-300 mb-3">With VeriWatch Pro you get:</p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-slate-300">
                <Bot className="w-4 h-4 text-teal-400" />
                Tank & Daisy AI assistants
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <Volume2 className="w-4 h-4 text-teal-400" />
                Voice alerts & briefings
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <Radio className="w-4 h-4 text-teal-400" />
                All 50 states frequencies
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <Bell className="w-4 h-4 text-teal-400" />
                Real-time push alerts
              </li>
            </ul>
          </div>
          
          <div className="flex flex-col gap-2">
            <Link to="/pricing" className="w-full">
              <Button className="w-full bg-teal-600 hover:bg-teal-700">
                <Zap className="w-4 h-4 mr-2" />
                View Plans - Starting at $10/mo
              </Button>
            </Link>
            <Button variant="ghost" onClick={onClose} className="w-full text-slate-400">
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Locked feature overlay
export const LockedOverlay = ({ 
  feature, 
  children,
  blurred = true 
}) => {
  const { hasFeature } = useSubscription();
  
  if (hasFeature(feature)) {
    return children;
  }
  
  return (
    <div className="relative">
      <div className={blurred ? 'blur-sm pointer-events-none select-none' : 'opacity-50 pointer-events-none'}>
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm rounded-lg">
        <UpgradePrompt feature={feature} compact />
      </div>
    </div>
  );
};

// Feature gate component - shows content or upgrade prompt
export const FeatureGate = ({ 
  feature, 
  children, 
  fallback = null 
}) => {
  const { hasFeature } = useSubscription();
  
  if (hasFeature(feature)) {
    return children;
  }
  
  if (fallback) {
    return fallback;
  }
  
  return <UpgradePrompt feature={feature} />;
};

// Subscription badge
export const SubscriptionBadge = ({ className = '' }) => {
  const { tier, getTierInfo } = useSubscription();
  const info = getTierInfo();
  
  const colors = {
    slate: 'bg-slate-700 text-slate-300',
    teal: 'bg-teal-600 text-white',
    amber: 'bg-amber-600 text-white'
  };
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colors[info.color]} ${className}`}>
      {tier === 'lifetime' && <Crown className="w-3 h-3" />}
      {tier === 'pro' && <Zap className="w-3 h-3" />}
      {info.displayName}
    </span>
  );
};

export default UpgradePrompt;
